<template>
    <div class="trip-leg-list-item text-body-1" :class="cssClasses">
        <v-row v-if="!first && !isNonTransit && previousLeg?.tripMode !== TripMode.Foot" no-gutters class="flex-nowrap">
            <v-col cols="9" class="ml-4">
                <div class="trip-leg-list-item__subtitle pt-8 pl-2">
                    {{ t('trips.resources.tripLegLabels.waitAt', 'Wait at') }}
                </div>
            </v-col>
        </v-row>

        <v-row v-if="!isNonTransit || first" no-gutters class="flex-nowrap">
            <v-col cols="auto" class="trip-leg-list-item__col">
                <div class="trip-leg-list-item__start" :class="iconClass">
                    <div class="trip-leg-list-item__icon bg-white"></div>
                </div>
            </v-col>
            <v-col cols="auto" class="d-flex align-center flex-nowrap flex-shrink-1 text-truncate">
                <quay-signage
                    v-if="modelValue.from.quay"
                    :icon="getTransportModeIcon(modelValue.from.quay.transitInfo)"
                    :color="getTransportModeIconColor(modelValue.from.quay.transportMode)"
                    icon-size="22"
                    class="mr-1 pt-1 flex-shrink-0"
                />
                <div class="trip-leg-list-item__place-name mr-1 text-no-wrap text-truncate">
                    <span class="text-h5 font-weight-bold">{{ startTitle }}</span>
                    <span v-if="modelValue.from.isMyLocation" class="text-h5 font-weight-regular ml-1">
                        ({{ t('places.resources.yourPosition', 'Your location') }})
                    </span>
                </div>
                <platform-label :title="modelValue.from.quay?.publicCode" size="22" class="flex-shrink-0" />
            </v-col>
            <v-spacer />
            <v-col
                v-if="previousLeg && !isTransitMode(previousLeg?.tripMode) && !previousLegIsTransfer"
                cols="auto"
                class="d-flex align-center justify-end ml-1"
            >
                <v-icon icon="kdi-foot" size="24" />
                <div class="trip-leg-list-item__duration text-16 text-md-18">
                    {{ getNonTransitDurationText(previousLeg.tripMode, previousLeg.duration) }}
                </div>
            </v-col>
            <v-col v-else-if="first" cols="auto" class="d-flex align-center justify-end ml-2">
                <span class="d-flex align-center">
                    <realtime-indicator v-if="isRealtime" class="mr-1" />
                    <div class="trip-leg-list-item__time text-16 text-md-18" :class="{ 'font-weight-bold': isRealtime }">
                        {{ startTime }}
                    </div>
                </span>
            </v-col>
        </v-row>

        <v-row v-if="!isNonTransit" no-gutters class="flex-nowrap">
            <v-col cols="auto" class="trip-leg-list-item__col" :class="iconClass">
                <div class="trip-leg-list-item__middle"></div>
            </v-col>
            <v-col cols="9">
                <div class="trip-leg-list-item__subtitle pt-8">
                    {{ getTripStartText(modelValue.tripMode, modelValue.transitInfo) }}
                </div>
            </v-col>
            <v-spacer />
        </v-row>

        <template v-if="isTransfer && previousLeg && nextLeg">
            <v-row no-gutters class="flex-nowrap">
                <v-col cols="auto" class="trip-leg-list-item__col" :class="iconClass">
                    <div class="trip-leg-list-item__middle" style="height: 34px"></div>
                </v-col>
            </v-row>
            <transfer-notice :from-leg="previousLeg" :to-leg="nextLeg" height="26px" :transfer-duration="modelValue.duration" />
        </template>

        <v-row no-gutters class="flex-nowrap">
            <v-col cols="auto" class="trip-leg-list-item__col" :class="iconClass">
                <div class="trip-leg-list-item__middle" :style="{ height: isTransfer && showTransferTime ? '34px' : undefined }"></div>
            </v-col>
            <v-col cols="auto" :class="{ 'd-flex align-end': isNonTransit }">
                <div v-if="isNonTransit" class="trip-leg-list-item__subtitle">
                    <template v-if="isSameQuay">{{ t('trips.resources.tripLegLabels.waitAt', 'Wait at') }}</template>
                    <template v-else>{{ getTripMiddleText(modelValue.tripMode) }}</template>
                </div>
                <div v-else>
                    <div v-if="!isNonTransit" class="py-1">
                        <div class="d-flex flex-nowrap">
                            <route-number :title="modelValue.lineName" fixed-size class="mr-2" />
                            <trip-headsign :title="titleAndSubtitle.title" :subtitle="titleAndSubtitle.subtitle" />
                        </div>
                    </div>
                </div>
            </v-col>
            <v-spacer />
            <v-col v-if="!isNonTransit" cols="auto" class="d-flex align-center justify-end ml-2">
                <stop-time-tooltip
                    :expected-time="modelValue.expectedStartTime"
                    :expected-time-is-realtime="modelValue.realtime"
                    :aimed-time="modelValue.aimedStartTime"
                >
                    <span class="d-flex align-center">
                        <realtime-indicator v-if="isRealtime" class="mr-1" />
                        <div class="trip-leg-list-item__time text-16 text-md-18" :class="{ 'font-weight-bold': isRealtime }">
                            {{ startTime }}
                        </div>
                    </span>
                </stop-time-tooltip>
            </v-col>
        </v-row>

        <v-row v-if="!isNonTransit" no-gutters class="flex-nowrap">
            <v-col cols="auto" class="trip-leg-list-item__col" :class="iconClass">
                <div class="trip-leg-list-item__middle"></div>
            </v-col>
            <v-col cols="12">
                <div class="pb-4">
                    <notification-messages-list
                        :messages="modelValue.notificationMessages"
                        :compact="isMobile"
                        hide-notification-body
                        class="mr-9 my-1"
                    />
                    <v-btn
                        v-if="modelValue.intermediateEstimatedCalls.length"
                        variant="outlined"
                        size="small"
                        :color="color"
                        class="pa-1 pr-2 my-1"
                        @click="expanded = !expanded"
                    >
                        {{ modelValue.intermediateEstimatedCalls.length }} {{ t('common.resources.distanceStops', 'stops') }}

                        <template #append>
                            <v-icon :icon="expanded ? 'kdi-collapse-vertical' : 'kdi-expand-vertical'" size="12" />
                        </template>
                    </v-btn>
                </div>
            </v-col>
        </v-row>

        <v-expand-transition v-if="modelValue.intermediateEstimatedCalls.length">
            <div v-if="expanded">
                <v-row
                    v-for="call in modelValue.intermediateEstimatedCalls"
                    :key="call.id"
                    no-gutters
                    class="trip-leg-list-item__call flex-nowrap"
                >
                    <v-col cols="auto" class="trip-leg-list-item__col" :class="iconClass">
                        <div class="trip-leg-list-item__middle">
                            <div class="trip-leg-list-item__icon bg-white"></div>
                        </div>
                    </v-col>
                    <v-col cols="auto" class="trip-leg-list-item__subtitle text-truncate d-flex align-center flex-shrink-1 py-2">
                        <div class="text-truncate">
                            {{ call.quay.name }}
                            <template v-if="!call.forAlighting">
                                ({{ t('common.resources.estimatedCall.noAlighting', 'no alighting') }})
                            </template>
                            <template v-else-if="!call.forBoarding">
                                ({{ t('common.resources.estimatedCall.noBoarding', 'no boarding') }})
                            </template>
                        </div>
                    </v-col>
                    <v-col cols="auto" class="trip-leg-list-item__subtitle d-flex align-center font-weight-regular pl-1">
                        - {{ toLocaleTimeString(call.isCallingSoon && call.expectedStopTime ? call.expectedStopTime : call.aimedStopTime) }}
                    </v-col>
                </v-row>
            </div>
        </v-expand-transition>

        <v-row v-if="!isNonTransit" no-gutters class="flex-nowrap">
            <v-col cols="auto" class="trip-leg-list-item__col" :class="iconClass">
                <div class="trip-leg-list-item__middle pt-11"></div>
            </v-col>
            <v-col cols="9" class="d-flex align-end">
                <div class="trip-leg-list-item__subtitle">
                    {{ getTripEndText(modelValue.tripMode, modelValue.transitInfo) }}
                </div>
            </v-col>
        </v-row>

        <v-row v-if="!isNonTransit || last" no-gutters class="flex-nowrap">
            <v-col cols="auto" class="trip-leg-list-item__col">
                <div class="trip-leg-list-item__end" :class="iconClass">
                    <div class="trip-leg-list-item__icon bg-white"></div>
                </div>
            </v-col>
            <v-col cols="auto" class="d-flex flex-nowrap flex-shrink-1 text-truncate">
                <quay-signage
                    v-if="modelValue.to.quay"
                    :icon="getTransportModeIcon(modelValue.to.quay.transitInfo)"
                    :color="getTransportModeIconColor(modelValue.to.quay.transportMode)"
                    icon-size="22"
                    class="mr-1 pt-1"
                />
                <div class="trip-leg-list-item__place-name mr-1 text-no-wrap text-truncate">
                    <span class="text-h5 font-weight-bold">{{ endTitle }}</span>
                    <span v-if="modelValue.to.isMyLocation" class="text-h5 font-weight-regular ml-1">
                        ({{ t('places.resources.yourPosition', 'Your location') }})
                    </span>
                </div>
                <platform-label :title="modelValue.to.quay?.publicCode" size="22" class="flex-shrink-0" />
            </v-col>
            <v-spacer />
            <v-col v-if="last && !isTransitMode(modelValue.tripMode)" cols="auto" class="d-flex align-center justify-end ml-1">
                <v-icon icon="kdi-foot" size="24" />
                <div class="trip-leg-list-item__duration text-16 text-md-18">
                    {{ getNonTransitDurationText(modelValue.tripMode, modelValue.duration) }}
                </div>
            </v-col>
            <v-col v-else cols="auto" class="d-flex align-center justify-end ml-2">
                <stop-time-tooltip
                    :expected-time="modelValue.expectedEndTime"
                    :expected-time-is-realtime="modelValue.realtime"
                    :aimed-time="modelValue.aimedEndTime"
                >
                    <span class="d-flex align-center">
                        <realtime-indicator v-if="isRealtime" class="mr-1" />
                        <div class="trip-leg-list-item__time text-16 text-md-18" :class="{ 'font-weight-bold': isRealtime }">
                            {{ endTime }}
                        </div>
                    </span>
                </stop-time-tooltip>
            </v-col>
        </v-row>

        <div v-if="last && !isTransitMode(modelValue.tripMode)" class="d-flex justify-end mt-4">
            <span class="text-h6 text-primary">{{ t('trips.resources.tripLegLabels.arriveAt', [endTime], 'Arrive at {0}') }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { type LegDto, TripMode, type PlaceDto, PlaceType } from '@/types/webapi';
import { computed, ref } from 'vue';
import { TripHeadsign, NotificationMessagesList, RealtimeIndicator, StopTimeTooltip } from '@/features/common/components';
import { toLocaleTimeString } from '@geta/kolumbus-frontend/composables';
import { QuaySignage, RouteNumber, PlatformLabel } from '@geta/kolumbus-frontend/components';
import { useI18n } from 'vue-i18n';
import { splitHeadsign, useKolumbusIcons } from '@/features/common/composables';
import { useTripModeHelper } from '@/features/trips/composables';
import { isTransitMode } from '@/features/trips/helpers';
import { TransferNotice } from '.';
import { useDisplay } from 'vuetify';

interface Props {
    /** The leg data to display */
    modelValue: LegDto;

    /** Whether this is the first leg in the trip */
    first?: boolean;

    /** Whether this is the last leg in the trip */
    last?: boolean;

    /** The previous leg in the trip, if any */
    previousLeg?: LegDto;

    /** The next leg in the trip, if any */
    nextLeg?: LegDto;
}

const props = defineProps<Props>();
const { t } = useI18n();
const { mobile: isMobile } = useDisplay();
const expanded = ref(false);
const isNonTransit = computed(() => !isTransitMode(props.modelValue.tripMode));
const isTransfer = computed(() => isNonTransit.value && !props.first && !props.last);
const { getTransportModeIcon, getTransportModeIconColor } = useKolumbusIcons();
const { getTripStartText, getTripMiddleText, getTripEndText, getNonTransitDurationText } = useTripModeHelper();
const showTransferTime = computed(() => isTransfer.value && props.previousLeg && props.nextLeg);
const previousLegIsTransfer = computed(() => props.previousLeg?.duration === 0 && props.previousLeg?.distance === 0);
const isSameQuay = computed(() => props.modelValue.from.quay?.id === props.modelValue.to.quay?.id);
const isRealtime = computed(() => props.modelValue.realtime && (props.modelValue.isStartingSoon || props.modelValue.isEndingSoon));

const cssClasses = computed(() => [
    {
        'trip-leg-list-item--first': props.first,
        'trip-leg-list-item--last': props.last,
        'trip-leg-list-item--with-transfer-time': showTransferTime.value
    },
    `trip-leg-list-item--${TripMode[props.modelValue.tripMode].toLowerCase()}`
]);

function getTitle(place: PlaceDto) {
    switch (place.type) {
        case PlaceType.Point:
        case PlaceType.TransitStation:
            return place.name;
        default:
            return `${place.coordinates.latitude}, ${place.coordinates.longitude}`;
    }
}

const startTime = computed(() =>
    toLocaleTimeString(props.modelValue.isStartingSoon ? props.modelValue.expectedStartTime : props.modelValue.aimedStartTime)
);
const startTitle = computed(() => getTitle(props.modelValue.from));
const endTime = computed(() =>
    toLocaleTimeString(props.modelValue.isEndingSoon ? props.modelValue.expectedEndTime : props.modelValue.aimedEndTime)
);
const endTitle = computed(() => getTitle(props.modelValue.to));
const color = computed(() => (!isTransitMode(props.modelValue.tripMode) ? 'blue' : 'primary'));
const iconClass = computed(() => ({ [`text-${color.value}`]: true }));
const titleAndSubtitle = computed(() => splitHeadsign(props.modelValue.tripHeadsign));

// allow collapsing from outside
defineExpose({ expanded });
</script>

<style lang="scss">
@use '@/styles/mixins' as *;
@use 'vuetify/settings' as *;
@use 'vuetify/lib/styles/tools/index' as tools;

$circle-size: 15px;
$circle-width: 4px;
$line-width: 5px;
$dotted-line-width: 7px;

.details-msg {
    width: 420px;
}

.trip-leg-list-item {
    &__col {
        width: 16px;
        margin-right: $spacer * 4;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__icon {
        @include circle($circle-size, $circle-width);
        color: currentColor !important;
        position: absolute;
    }

    &__start,
    &__end,
    &__middle {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    &__start,
    &__end {
        height: 100%;

        &::before,
        &::after {
            content: '';
            flex-grow: 1;
        }
    }

    &:not(.trip-leg-list-item--foot):not(.trip-leg-list-item--bicycle) {
        .trip-leg-list-item__start::after,
        .trip-leg-list-item__end::before {
            @include vertical-line(currentColor, $line-width);
        }
    }

    &__middle {
        margin-top: -3px;
        margin-bottom: -3px;
        height: calc(100% + 6px);
        width: 16px;

        &::before {
            content: '';
            width: $line-width;
            height: 100%;
            background-color: currentColor;
            position: absolute;
            top: 0;
        }

        .trip-leg-list-item--foot &,
        .trip-leg-list-item--bicycle & {
            height: 48px;

            &::before {
                content: '';
                background: radial-gradient(30% 30%, var(--v-color-blue) 99%, transparent calc(99% + 1px));
                background-size: $dotted-line-width $dotted-line-width;
                background-repeat: repeat-y;
                background-position: top;
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0;
            }
        }
    }

    .trip-headsign {
        --title-font-size: #{calc(18px / $font-size-root) * 1rem};
        --subtitle-font-size: #{calc(14px / $font-size-root) * 1rem};

        &__subtitle {
            color: var(--v-color-grey-darken-2, #657785);
        }
    }
}
</style>
